import { Logger } from '@utils/logger';
import { Injectable } from '@angular/core';
import fundContentQuery from '@graphql/overview/fund-content.graphql';
import { FundOverviewService } from '@products/services/fund-overview.service';
import { Observable } from 'rxjs';
import { SiteConfigService } from '@services/site-config.service';
import { map } from 'rxjs/operators';
import { FundDataServiceParams } from '@products/services/graphql-fund-data.service';
import { Product } from '@models';

const logger = Logger.getLogger('FundContentService');

@Injectable({
  providedIn: 'root',
})
export class FundContentService {
  private riskFactorContentCategory: string;
  private regulatorLinkUrl: string;
  constructor(
    private siteConfigService: SiteConfigService,
    private fundOverviewService: FundOverviewService
  ) {
    this.riskFactorContentCategory = siteConfigService.getRiskFactorContentCategory();
    this.regulatorLinkUrl = siteConfigService.getRegulatorLinkUrl();
  }

  /**
   * Call register method of fund overview service.
   * @param fundDataServiceParams received as fund parameters.
   * @param documentSet - set of FundContent documents returned in resourceApi.
   * @param location - location as set in FundContentComponent params.
   */
  public populate(
    fundDataServiceParams: FundDataServiceParams,
    documentSet: DocumentData[],
    location: string
  ): Observable<any> {
    return this.fundOverviewService
      .register(fundContentQuery, fundDataServiceParams)
      .pipe(
        map((product: Product) => {
          logger.debug(product);
          if (product) {
            const categories: string[] = this.siteConfigService.getFundContentCategories(
              product.productType,
              location
            );
            let docs = [];
            if (categories) {
              docs = categories
                .map((category) =>
                  documentSet.find(
                    (contentDoc) => contentDoc.category === category
                  )
                )
                .filter((doc) => doc !== undefined);
              // process widen assets
              docs.forEach((doc) => {
                this.processWidenAssets(doc.contentblocks);
                // WDE-6624 - set RegulatorlinkUrl to wrap the risk factor if available else set it to null
                doc.regulatorLinkURL = this.regulatorLinkUrl || null;
              });
              // NGC-14012: NextGen Canada Risk / Return Profile
              if (this.siteConfigService.isCanada()) {
                if (product.shareClasses && product.shareClasses[0]) {
                  const riskFactor = Number(product.shareClasses[0].riskFactor);
                  if (!isNaN(riskFactor)) {
                    docs.forEach((doc) => {
                      doc.showRiskFactor =
                        doc.category === this.riskFactorContentCategory;
                      doc.variation = 3;
                      // doc.rank = riskFactor; Pull Risk Value in subscribe
                    });
                  }
                }
              } else {
                // check showRiskFactor
                if (
                  product.shareClasses &&
                  product.shareClasses[0] &&
                  product.shareClasses[0].riskFactor
                ) {
                  const riskFactor = Number(product.shareClasses[0].riskFactor);
                  if (!isNaN(riskFactor)) {
                    docs.forEach((doc) => {
                      doc.showRiskFactor =
                        doc.category === this.riskFactorContentCategory;
                      doc.variation =
                        this.siteConfigService.getPdsCountry() === 'NL' ? 2 : 1;
                      doc.rank = riskFactor;
                    });
                  }
                }
              }
            }
            return docs;
          }
        })
      );
  }

  private processWidenAssets(contentblocks: any) {
    contentblocks
      ?.filter(
        (cb) => cb.contentType === 'ftcore:ImageCompound' && cb.widenAsset
      )
      .forEach((cb) => {
        const widenAsset = JSON.parse(cb.widenAsset);
        cb.imageSrc = widenAsset.url;
      });

    // check 2 col items
    contentblocks
      ?.filter((cb) => cb.itemsTwo)
      .forEach((cb) => {
        this.processWidenAssets(cb.items);
        this.processWidenAssets(cb.itemsTwo);
      });
  }
}
